import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays,
  MailOpen,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Phone,
  MapPin,
  Filter,
  LogOut,
  ShieldCheck,
  ChevronDown,
  Check,
  X,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { KRAFT } from '@/constants/testIds';

const STATUS_META = {
  pending:   { label: 'Oczekująca',  ring: 'border-slate-500', dot: 'bg-slate-400', text: 'text-slate-300' },
  confirmed: { label: 'Potwierdzona', ring: 'border-volt',      dot: 'bg-volt',       text: 'text-volt' },
  completed: { label: 'Zakończona',   ring: 'border-emerald-500', dot: 'bg-emerald-400', text: 'text-emerald-300' },
  cancelled: { label: 'Anulowana',    ring: 'border-red-500',   dot: 'bg-red-500',    text: 'text-red-300' },
};

const STATUS_ORDER = ['pending', 'confirmed', 'completed', 'cancelled'];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(iso);
  }
};

export default function Admin() {
  const navigate = useNavigate();
  const { status: authStatus, user, logout } = useAuth();
  const [tab, setTab] = useState('bookings');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind: 'booking'|'message', item }

  // Redirect to login when unauthenticated
  useEffect(() => {
    if (authStatus === 'guest') navigate('/admin/login', { replace: true });
  }, [authStatus, navigate]);

  const load = async () => {
    if (authStatus !== 'authed') return;
    setLoading(true);
    try {
      const [b, m] = await Promise.all([
        api.get('/bookings', { params: { limit: 500 } }),
        api.get('/contact', { params: { limit: 500 } }),
      ]);
      setBookings(b.data || []);
      setMessages(m.data || []);
    } catch (e) {
      toast.error('Błąd wczytywania danych', { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authStatus === 'authed') load();
  }, [authStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (booking, newStatus) => {
    if (newStatus === booking.status) return;
    // Optimistic
    const prev = booking.status;
    setBookings((rows) => rows.map((r) => (r.id === booking.id ? { ...r, status: newStatus } : r)));
    try {
      await api.patch(`/admin/bookings/${booking.id}/status`, { status: newStatus, send_email: true });
      toast.success(`Status zmieniony · ${STATUS_META[newStatus].label}`, {
        description: `Powiadomienie e-mail wysłane do ${booking.email}.`,
      });
    } catch (e) {
      // rollback
      setBookings((rows) => rows.map((r) => (r.id === booking.id ? { ...r, status: prev } : r)));
      toast.error('Nie udało się zmienić statusu', { description: formatApiError(e) });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { kind, item } = confirmDelete;
    const endpoint = kind === 'booking' ? `/admin/bookings/${item.id}` : `/admin/messages/${item.id}`;
    // Optimistic
    if (kind === 'booking') {
      setBookings((rows) => rows.filter((r) => r.id !== item.id));
    } else {
      setMessages((rows) => rows.filter((r) => r.id !== item.id));
    }
    setConfirmDelete(null);
    try {
      await api.delete(endpoint);
      toast.success(kind === 'booking' ? 'Rezerwacja usunięta' : 'Wiadomość usunięta');
    } catch (e) {
      // Reload to restore state on failure
      toast.error('Nie udało się usunąć', { description: formatApiError(e) });
      load();
    }
  };

  const filteredBookings = useMemo(() => {
    const s = q.trim().toLowerCase();
    return bookings.filter((b) => {
      if (statusFilter !== 'all' && (b.status || 'pending') !== statusFilter) return false;
      if (!s) return true;
      return [b.name, b.email, b.phone, b.date, b.service, b.address]
        .some((v) => v && String(v).toLowerCase().includes(s));
    });
  }, [bookings, q, statusFilter]);

  const filteredMessages = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return messages;
    return messages.filter((m) =>
      [m.name, m.email, m.phone, m.subject, m.message]
        .some((v) => v && String(v).toLowerCase().includes(s))
    );
  }, [messages, q]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      totalBookings: bookings.length,
      todayBookings: bookings.filter((b) => b.date === today).length,
      pending: bookings.filter((b) => (b.status || 'pending') === 'pending').length,
      totalMessages: messages.length,
    };
  }, [bookings, messages]);

  // Auth loading screen
  if (authStatus !== 'authed') {
    return (
      <div className="min-h-screen bg-ink-950 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin text-volt" />
          <span className="font-mono uppercase tracking-[0.22em] text-xs">Weryfikacja sesji...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={KRAFT.adminRoot} className="min-h-screen bg-ink-950">
      {/* Sticky header */}
      <header className="border-b border-white/10 bg-ink-900/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="w-10 h-10 border border-white/10 flex items-center justify-center text-white hover:border-volt hover:text-volt transition-colors"
              aria-label="Wróć na stronę główną"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="leading-tight">
                <div className="font-display uppercase text-white text-lg tracking-tight">Panel Admina</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">Chroniony obszar</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              data-testid={KRAFT.adminUserBadge}
              className="hidden md:flex items-center gap-2 px-3 py-2 border border-white/10"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-volt" />
              <span className="font-mono text-[11px] text-slate-200">{user?.email}</span>
            </div>
            <button
              data-testid={KRAFT.adminRefresh}
              onClick={load}
              disabled={loading}
              className="btn-outline !py-2 !px-4 !text-[11px] disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Odśwież
            </button>
            <button
              data-testid={KRAFT.adminLogout}
              onClick={async () => { await logout(); navigate('/admin/login', { replace: true }); }}
              className="btn-outline !py-2 !px-4 !text-[11px]"
            >
              <LogOut className="w-4 h-4" />
              Wyloguj
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 md:py-14">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          <StatCard label="Rezerwacje" value={stats.totalBookings} accent="volt" />
          <StatCard label="Do potwierdzenia" value={stats.pending} accent="pending" />
          <StatCard label="Dziś" value={stats.todayBookings} />
          <StatCard label="Wiadomości" value={stats.totalMessages} accent="volt" />
        </div>

        {/* Tabs + filter */}
        <div className="mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex bg-ink-900 border border-white/10 divide-x divide-white/10">
            <button
              data-testid={KRAFT.adminTabBookings}
              onClick={() => setTab('bookings')}
              className={`px-5 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                tab === 'bookings' ? 'bg-volt text-ink-950' : 'text-slate-300 hover:text-white'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Rezerwacje
              <span className={`ml-2 px-2 py-0.5 text-[10px] ${tab === 'bookings' ? 'bg-ink-950 text-volt' : 'bg-white/10 text-slate-200'}`}>
                {filteredBookings.length}
              </span>
            </button>
            <button
              data-testid={KRAFT.adminTabMessages}
              onClick={() => setTab('messages')}
              className={`px-5 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                tab === 'messages' ? 'bg-volt text-ink-950' : 'text-slate-300 hover:text-white'
              }`}
            >
              <MailOpen className="w-4 h-4" />
              Wiadomości
              <span className={`ml-2 px-2 py-0.5 text-[10px] ${tab === 'messages' ? 'bg-ink-950 text-volt' : 'bg-white/10 text-slate-200'}`}>
                {filteredMessages.length}
              </span>
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            {tab === 'bookings' && (
              <div className="flex bg-ink-900 border border-white/10 divide-x divide-white/10 overflow-x-auto">
                <StatusPill
                  active={statusFilter === 'all'}
                  onClick={() => setStatusFilter('all')}
                  label={`Wszystkie (${bookings.length})`}
                />
                {STATUS_ORDER.map((s) => (
                  <StatusPill
                    key={s}
                    status={s}
                    active={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                    label={STATUS_META[s].label}
                  />
                ))}
              </div>
            )}
            <div className="relative">
              <Filter className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Szukaj..."
                className="w-full md:w-72 pl-10 pr-4 py-3 bg-ink-900 border border-white/10 text-white text-sm font-mono placeholder:text-slate-500 focus:outline-none focus:border-volt"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-8">
          {loading && bookings.length === 0 && messages.length === 0 ? (
            <div className="py-24 flex items-center justify-center text-slate-400 gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-volt" />
              <span className="font-mono uppercase tracking-[0.2em] text-xs">Ładowanie danych...</span>
            </div>
          ) : tab === 'bookings' ? (
            <BookingsTable
              rows={filteredBookings}
              onStatusChange={handleStatusChange}
              onDelete={(item) => setConfirmDelete({ kind: 'booking', item })}
            />
          ) : (
            <MessagesTable
              rows={filteredMessages}
              onDelete={(item) => setConfirmDelete({ kind: 'message', item })}
            />
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-ink-950/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setConfirmDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-md bg-ink-900 border border-red-500/50 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent" />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 border border-red-500 bg-red-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-red-400">Nieodwracalne</div>
                    <div className="font-display uppercase text-white text-xl tracking-tight leading-tight">
                      Potwierdź usunięcie
                    </div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {confirmDelete.kind === 'booking' ? (
                    <>
                      Usunąć rezerwację <span className="text-white font-semibold">{confirmDelete.item.name}</span> na
                      <span className="text-volt font-mono"> {confirmDelete.item.date} · {confirmDelete.item.time_slot}</span>?
                      Ta operacja jest nieodwracalna — klient nie otrzyma powiadomienia.
                    </>
                  ) : (
                    <>
                      Usunąć wiadomość od <span className="text-white font-semibold">{confirmDelete.item.name}</span>
                      {confirmDelete.item.subject && (
                        <> („<span className="text-slate-100">{confirmDelete.item.subject}</span>&rdquo;)</>
                      )}
                      ? Ta operacja jest nieodwracalna.
                    </>
                  )}
                </p>
                <div className="flex items-center gap-3 mt-8">
                  <button
                    data-testid={KRAFT.adminDeleteConfirm}
                    onClick={handleDelete}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-mono text-[11px] uppercase tracking-[0.1em] font-semibold transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Tak, usuń
                  </button>
                  <button
                    data-testid={KRAFT.adminDeleteCancel}
                    onClick={() => setConfirmDelete(null)}
                    className="btn-outline !py-3 !px-5 !text-[11px]"
                  >
                    <X className="w-4 h-4" />
                    Anuluj
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StatusPill({ status, active, onClick, label }) {
  const dot = status ? STATUS_META[status]?.dot : 'bg-white/40';
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] whitespace-nowrap transition-colors ${
        active ? 'bg-volt/10 text-white' : 'text-slate-400 hover:text-white'
      }`}
    >
      {status && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
      {label}
    </button>
  );
}

function StatCard({ label, value, accent }) {
  const color =
    accent === 'volt' ? 'text-volt' : accent === 'pending' ? 'text-slate-100' : 'text-white';
  return (
    <div className="bg-ink-950 p-6 md:p-8">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-3">{label}</div>
      <div className={`font-display uppercase text-3xl md:text-4xl leading-none tracking-tight ${color}`}>
        {value}
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-white/10 bg-ink-900 py-20 text-center">
      <div className="font-mono uppercase tracking-[0.22em] text-xs text-slate-400">{label}</div>
    </div>
  );
}

function StatusDropdown({ booking, onChange }) {
  const [open, setOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const current = booking.status || 'pending';
  const meta = STATUS_META[current];

  useEffect(() => {
    const onDoc = (e) => {
      if (!e.target.closest?.(`[data-dd-scope="${booking.id}"]`)) setOpen(false);
    };
    if (open) document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open, booking.id]);

  const commit = async (newStatus) => {
    setPendingChange(null);
    setOpen(false);
    await onChange(booking, newStatus);
  };

  return (
    <div className="relative" data-dd-scope={booking.id}>
      <button
        data-testid={KRAFT.adminStatusSelect(booking.id)}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-2 px-3 py-2 border ${meta.ring} bg-ink-950 text-xs font-mono uppercase tracking-wider ${meta.text}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
        {meta.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 bg-ink-900 border border-white/10 z-30 shadow-xl"
          >
            {STATUS_ORDER.map((s) => {
              const m = STATUS_META[s];
              const isCurrent = s === current;
              return (
                <button
                  key={s}
                  data-testid={KRAFT.adminStatusOption(s)}
                  disabled={isCurrent}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCurrent) return;
                    // Confirm destructive changes
                    if (s === 'cancelled' || s === 'completed') {
                      setPendingChange(s);
                    } else {
                      commit(s);
                    }
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left border-b border-white/5 last:border-b-0 transition-colors ${
                    isCurrent ? 'bg-ink-950/60 cursor-default' : 'hover:bg-volt/10'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                    <span className={`font-mono text-xs uppercase tracking-wider ${m.text}`}>{m.label}</span>
                  </span>
                  {isCurrent && <Check className="w-3.5 h-3.5 text-volt" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation for destructive changes */}
      <AnimatePresence>
        {pendingChange && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="absolute right-0 mt-2 w-80 bg-ink-900 border border-volt/60 p-4 z-30 shadow-2xl"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt mb-2">Potwierdzenie</div>
            <p className="text-slate-200 text-sm leading-relaxed">
              Zmienić status na <span className={STATUS_META[pendingChange].text}>{STATUS_META[pendingChange].label}</span>?
              Klient otrzyma powiadomienie e-mail.
            </p>
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => commit(pendingChange)}
                className="btn-volt !py-2 !px-4 !text-[11px] flex-1 justify-center"
              >
                <Check className="w-3.5 h-3.5" />
                Tak, zmień
              </button>
              <button
                onClick={() => setPendingChange(null)}
                className="btn-outline !py-2 !px-4 !text-[11px]"
              >
                <X className="w-3.5 h-3.5" />
                Anuluj
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BookingsTable({ rows, onStatusChange, onDelete }) {
  if (!rows.length) return <EmptyState label="Brak rezerwacji spełniających kryteria" />;
  return (
    <div className="border border-white/10 bg-ink-900 overflow-x-auto overflow-y-visible">
      <table className="min-w-full">
        <thead className="bg-ink-950 border-b border-white/10">
          <tr>
            <Th>Termin</Th>
            <Th>Klient</Th>
            <Th>Kontakt</Th>
            <Th>Zakres</Th>
            <Th>Adres</Th>
            <Th>Status</Th>
            <Th>Akcje</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b, idx) => (
            <motion.tr
              key={b.id}
              data-testid={KRAFT.adminBookingRow(b.id)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: idx * 0.02 }}
              className="border-b border-white/5 hover:bg-ink-950 transition-colors"
            >
              <Td>
                <div className="font-display uppercase text-volt text-lg leading-tight">{b.date}</div>
                <div className="font-mono text-xs text-slate-300">{b.time_slot}</div>
                <div className="font-mono text-[10px] text-slate-500 mt-1">
                  utw. {formatDate(b.created_at)}
                </div>
              </Td>
              <Td>
                <div className="text-white text-sm">{b.name}</div>
              </Td>
              <Td>
                <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-slate-200 text-sm hover:text-volt">
                  <Phone className="w-3 h-3 text-volt" />
                  <span className="font-mono">{b.phone}</span>
                </a>
                <a href={`mailto:${b.email}`} className="block font-mono text-xs text-slate-400 hover:text-white mt-1">
                  {b.email}
                </a>
              </Td>
              <Td>
                <span className="text-slate-200 text-sm">{b.service || '—'}</span>
                {b.notes && (
                  <div className="text-slate-500 text-xs mt-2 max-w-[240px] line-clamp-2">
                    {b.notes}
                  </div>
                )}
              </Td>
              <Td>
                {b.address ? (
                  <div className="flex items-start gap-2 text-slate-300 text-sm max-w-[220px]">
                    <MapPin className="w-3 h-3 text-volt mt-1 shrink-0" />
                    <span>{b.address}</span>
                  </div>
                ) : (
                  <span className="text-slate-500">—</span>
                )}
              </Td>
              <Td>
                <StatusDropdown booking={b} onChange={onStatusChange} />
              </Td>
              <Td>
                <button
                  data-testid={KRAFT.adminDeleteBooking(b.id)}
                  onClick={() => onDelete(b)}
                  className="w-9 h-9 border border-white/10 text-slate-400 hover:border-red-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors"
                  aria-label="Usuń rezerwację"
                  title="Usuń rezerwację"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </Td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MessagesTable({ rows, onDelete }) {
  if (!rows.length) return <EmptyState label="Brak wiadomości do wyświetlenia" />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
      {rows.map((m, idx) => (
        <motion.article
          key={m.id}
          data-testid={KRAFT.adminMessageRow(m.id)}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: idx * 0.03 }}
          className="bg-ink-950 p-6 relative group"
        >
          <button
            data-testid={KRAFT.adminDeleteMessage(m.id)}
            onClick={() => onDelete(m)}
            className="absolute top-4 right-4 w-9 h-9 border border-white/10 text-slate-400 hover:border-red-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label="Usuń wiadomość"
            title="Usuń wiadomość"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="flex items-start justify-between gap-4 mb-3 pr-12">
            <div>
              <div className="font-display uppercase text-white text-lg tracking-tight">{m.name}</div>
              <div className="font-mono text-xs text-slate-400 mt-1">{m.subject || '— brak tematu —'}</div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 shrink-0">
              {formatDate(m.created_at)}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pb-4 mb-4 border-b border-white/5">
            <a href={`mailto:${m.email}`} className="font-mono text-xs text-volt hover:underline">{m.email}</a>
            {m.phone && (
              <a href={`tel:${m.phone}`} className="flex items-center gap-1.5 text-slate-300 hover:text-white">
                <Phone className="w-3 h-3 text-volt" />
                <span className="font-mono text-xs">{m.phone}</span>
              </a>
            )}
          </div>
          <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
        </motion.article>
      ))}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-5 py-4 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td className="px-5 py-4 align-top">{children}</td>;
}
